/**
 * User Activity Tab
 * Displays user activity, usage stats, and history
 */

import { User } from '../../api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { StatsGrid, StatsCard } from '@/components/shared/StatsCard'
import { Activity, MessageSquare, Zap, DollarSign } from 'lucide-react'
import { formatNumber, formatCurrency } from '@/lib/utils/format'

interface UserActivityTabProps {
  user: User
}

export function UserActivityTab({ user }: UserActivityTabProps) {
  return (
    <div className="space-y-6">
      {/* Usage Stats */}
      <StatsGrid>
        <StatsCard
          title="Tokens Consumed"
          value={user.tokens_consumed || 0}
          icon={<Zap className="h-4 w-4" />}
          format="number"
        />
        <StatsCard
          title="Total Cost"
          value={user.cost || 0}
          icon={<DollarSign className="h-4 w-4" />}
          format="currency"
        />
        <StatsCard
          title="Total Agents"
          value={user.total_agents || 0}
          icon={<Activity className="h-4 w-4" />}
          format="number"
        />
      </StatsGrid>

      {/* Usage Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Usage Summary
          </CardTitle>
          <CardDescription>Overall usage statistics for this user</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="text-sm font-medium text-muted-foreground">
                  Tokens Consumed
                </div>
                <div className="text-2xl font-bold">
                  {formatNumber(user.tokens_consumed || 0)}
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-sm font-medium text-muted-foreground">
                  Total Cost
                </div>
                <div className="text-2xl font-bold">
                  {formatCurrency(user.cost || 0)}
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-sm font-medium text-muted-foreground">
                  Agents Created
                </div>
                <div className="text-2xl font-bold">{user.total_agents || 0}</div>
              </div>

              <div className="space-y-1">
                <div className="text-sm font-medium text-muted-foreground">
                  Current Balance
                </div>
                <div className="text-2xl font-bold">
                  {formatNumber(user.tokenCredits || 0)} tokens
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Activity Timeline
          </CardTitle>
          <CardDescription>Recent user activity and events</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Activity Timeline Coming Soon</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Detailed activity logs, conversation history, and event timeline will be
              available in a future update.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
